import config from 'config'
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'
import { TextBox } from 'srcReact/components/common2/TextBox'
import { isEmpty } from 'srcReact/helpers/validator'
import { getColorHexCode } from 'srcReact/helpers/utils'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import { workplaceSettingsActions } from 'srcReact/WorkplaceSettingsPage'
import { GoogleMap } from 'srcReact/components'
import ColorPicker from 'srcReact/components/common2/ColorPicker'
import Button from 'srcReact/components/common2/Button'
import Spinner from 'srcReact/components/common2/Spinner'
import LocationTextBox from 'srcReact/components/common2/LocationTextBox'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'

const Display = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const {
    common: { isMobile },
    workplaceSettings: { display }
  } = useSelector((state) => state)

  const [showColorPicker, setShowColorPicker] = useState(false)
  const [nameChanged, setNameChanged] = useState(false)
  const [acronymChanged, setAcronymChanged] = useState(false)
  const [colorChanged, setColorChanged] = useState(false)
  const [locationChanged, setLocationChanged] = useState(false)
  const [workplaceColor, setWorkplaceColor] = useState(display?.color)
  const [workplaceLocation, setWorkplaceLocation] = useState({
    value: display?.location,
    hasError: false,
    errorMessage: '',
    isRequired: true
  })
  const [workplaceName, setWorkplaceName] = useState({
    value: display?.name || '',
    hasError: false,
    errorMessage: '',
    isRequired: false
  })
  const [workplaceAcronym, setWorkplaceAcronym] = useState({
    value: display?.accronym || '',
    hasError: false,
    errorMessage: '',
    isRequired: false
  })

  useEffect(() => {
    if (display.success && !display.loading) {
      setNameChanged(false)
      setAcronymChanged(false)
      setColorChanged(false)
      setLocationChanged(false)
    }
    if (display?.id > 0) {
      setWorkplaceName({ ...workplaceName, value: display?.name })
      setWorkplaceAcronym({ ...workplaceAcronym, value: display?.accronym })
      setWorkplaceColor(display?.color)
      setWorkplaceLocation({
        ...workplaceLocation,
        value: display?.location
      })
    }
  }, [display])

  const validate = () => {
    if (isEmpty(workplaceName.value)) {
      setWorkplaceName({
        ...workplaceName,
        hasError: true,
        errorMessage: t('error.required', { field: t('organizationSettings.display.labelOrganizationName') })
      })
      return false
    }
    if (isEmpty(workplaceAcronym.value)) {
      setWorkplaceAcronym({
        ...workplaceAcronym,
        hasError: true,
        errorMessage: t('error.required', { field: t('organizationSettings.display.labelAcronym') })
      })
      return false
    }
    return true
  }

  const getInitialPayload = () => {
    let payload = null
    const { id, name, accronym, color } = display
    payload = {
      workplace_id: id,
      name: name,
      accronym: accronym,
      color: color
    }
    return payload
  }

  const handleSave = (field) => {
    let payload = getInitialPayload()
    if (payload) {
      if (field === 'name') {
        payload = { ...payload, name: workplaceName.value, accronym: workplaceAcronym.value }
      } else if (field === 'color') {
        payload = { ...payload, color: workplaceColor }
      } else if (field === 'location') {
        payload = { ...payload, location: JSON.stringify(workplaceLocation?.value) }
      }
      if (validate()) {
        dispatch(workplaceSettingsActions.updateWorkplace(payload))
      }
    }
  }
  const handleCancel = (field) => {
    const { name, accronym, color, location } = display
    if (field === 'name') {
      setWorkplaceName({ ...workplaceName, value: name, hasError: false, errorMessage: '' })
      setWorkplaceAcronym({ ...workplaceAcronym, value: accronym, hasError: false, errorMessage: '' })
      setNameChanged(false)
      setAcronymChanged(false)
    } else if (field === 'color') {
      setWorkplaceColor(color)
      setColorChanged(false)
    } else if (field === 'location') {
      setWorkplaceLocation({
        ...workplaceLocation,
        value: location,
        hasError: false,
        errorMessage: ''
      })
      setLocationChanged(false)
    }
  }
  const handleNameChange = (e) => {
    setWorkplaceName({
      ...workplaceName,
      value: e.target.value,
      errorMessage: '',
      hasError: false
    })
    setNameChanged(true)
  }
  const handleAcronymChange = (e) => {
    setWorkplaceAcronym({
      ...workplaceAcronym,
      value: e.target.value,
      errorMessage: '',
      hasError: false
    })
    setAcronymChanged(true)
  }
  const handleColorChange = (e) => {
    const selectedColor = appConstantSettings.COLORS.find((c) => c.hex == e.hex)
    setWorkplaceColor(selectedColor?.name)
    setShowColorPicker(false)
    setColorChanged(true)
  }

  const handleClear = () => {
    setWorkplaceLocation({
      ...workplaceLocation,
      value: null,
      hasError: true,
      errorMessage: ''
    })
  }
  const handleLocationChange = (selectedLocation) => {
    setWorkplaceLocation({
      ...workplaceLocation,
      value: selectedLocation,
      hasError: false,
      errorMessage: ''
    })
    setLocationChanged(true)
  }

  const handleLocationBlur = (selectedLocation) => {
    if (!selectedLocation) {
      setWorkplaceLocation({
        ...setWorkplaceLocation,
        value: selectedLocation,
        hasError: true,
        errorMessage: t('error.required', { field: t(`formlabel.location`) })
      })
    }
  }
  const renderUpdateButtons = (field, className) => (
    <div className={`${styles.buttons} ${className}`}>
      <Button
        style='secondary'
        customStyle={styles.cancelBtn}
        text={t('global.cancel')}
        handleClick={() => handleCancel(field)}
      />
      <Button
        customStyle={styles.updateBtn}
        text={display?.loading ? <Spinner /> : t('global.update')}
        handleClick={() => handleSave(field)}
      />
    </div>
  )

  return (
    <div className={styles.displayWrapper}>
      <div className={styles.nameAccronym}>
        <h3 className={styles.title}>{t('workplaceSettings.display.name')}</h3>
        <p className={styles.description}>{t('workplaceSettings.display.nameDescription')}</p>
        <div className={styles.inputFields}>
          <div className={styles.inputName}>
            <TextBox
              name='name'
              fieldInfo={workplaceName}
              onChange={handleNameChange}
              customWrapperCss={styles.customWrapperCss}
              customInputCss={`${styles.inputClassName} ${workplaceName.hasError && styles.hasError}`}
              size='full'
              label={t('workplaceSettings.display.labelWorkplaceName')}
              labelPosition='bottom'
              customLabelCss={styles.customLabelCss}
              displayError={false}
              maxLength={appConstantSettings.WP_NAME_MAXLENGTH}
            />
          </div>
          {isMobile && nameChanged && renderUpdateButtons('name')}
          <div className={styles.inputAccronym}>
            <TextBox
              name='accronym'
              fieldInfo={workplaceAcronym}
              onChange={handleAcronymChange}
              customWrapperCss={styles.customWrapperCss}
              customInputCss={`${styles.inputClassName} ${workplaceAcronym.hasError && styles.hasError}`}
              size='full'
              label={t('workplaceSettings.display.labelAcronym')}
              labelPosition='bottom'
              customLabelCss={styles.customLabelCss}
              displayError={false}
              maxLength={appConstantSettings.ACCRONYM_MAXLENGHT}
            />
          </div>
          {((!isMobile && nameChanged) || acronymChanged) && renderUpdateButtons('name')}
        </div>
      </div>
      {workplaceName.errorMessage ? (
        <ErrorMessage text={workplaceName.errorMessage} />
      ) : workplaceAcronym.errorMessage ? (
        <ErrorMessage text={workplaceAcronym.errorMessage} />
      ) : null}

      <div className={styles.colorPicker}>
        <h3 className={styles.title}>{t('workplaceSettings.display.color')}</h3>
        <p className={styles.description}>{t('workplaceSettings.display.colorDescription')}</p>
        <div className={styles.colorInput}>
          <button className={styles.swatch} onClick={() => setShowColorPicker(!showColorPicker)}>
            <div
              className={styles.background}
              style={{
                backgroundColor: getColorHexCode(workplaceColor)
              }}
            />
          </button>
          {colorChanged && renderUpdateButtons('color', styles.colorUpdateButtons)}
        </div>
        {showColorPicker && (
          <ColorPicker
            colors={appConstantSettings.COLORS.map((c) => c.hex)}
            color={getColorHexCode(workplaceColor)}
            setColor={handleColorChange}
            setShowColorPicker={setShowColorPicker}
            showColorPicker={showColorPicker}
          />
        )}
      </div>

      <div className={styles.location}>
        <h3 className={styles.title}>{t('workplaceSettings.display.location')}</h3>
        <p className={styles.description}>{t('workplaceSettings.display.locationDescription')}</p>
        <div className={styles.locationWrapper}>
          <GoogleMap
            className={styles.googleMap}
            apiKey={config.googleFrontKey}
            lat={display?.location?.latitude}
            lng={display?.location?.longitude}
            zoom={15}
            title={display?.location?.name}
          />
          <div className={styles.inputLocation}>
            <LocationTextBox
              value={workplaceLocation}
              ariaLabel={t('workplace.create.placeholder_location')}
              placeholder={t('workplace.create.placeholder_location')}
              handleSelectLocation={handleLocationChange}
              handleSelectLocationBlur={handleLocationBlur}
              handleClear={handleClear}
              menuPortalTarget={true}
            />
          </div>
          {locationChanged && renderUpdateButtons('location')}
        </div>
      </div>
    </div>
  )
}

export default Display
